import React, { useState, useEffect } from "react";
import { Table, Modal, Button, Tooltip, Progress } from "antd";
import {
  EyeOutlined,
  UsergroupAddOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import assesmentService from "../../../services/assessmentService.ts";
import Constant from "../../../global/Constant.ts";
import { setLoading } from "../../../redux/globalSlice.ts";
import { userTypeKey } from "../../../global/enums.ts";

const AdminAssessmentManagement = () => {
  const [assessments, setAssessments] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [isParticipantModalVisible, setIsParticipantModalVisible] =
    useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const dispatch = useDispatch();

  const fetchAssessments = async () => {
    try {
      dispatch(setLoading(true));
      const payload = {
        page: pagination.current,
        limit: pagination.pageSize,
      };
      const response = await assesmentService.getAssessments(
        Constant?.assessment,
        payload
      );

      const formattedAssessments = response.assessments.map((assessment) => ({
        ...assessment,
        key: assessment.AssessmentID,
        progress: calculateProgress(assessment),
      }));

      setAssessments(formattedAssessments);
      setPagination((prev) => ({
        ...prev,
        total: response.totalRecords,
      }));
    } catch (error) {
      console.error("Error fetching assessments:", error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const calculateProgress = (assessment) => {
    const participationRate =
      assessment.ParticipantsCount > 0
        ? (assessment.AssessmentTakenCount / assessment.ParticipantsCount) * 100
        : 0;

    return (
      <Progress
        percent={Math.round(participationRate)}
        status={participationRate >= 50 ? "success" : "active"}
      />
    );
  };

  const handleViewParticipants = (assessment) => {
    setSelectedAssessment(assessment?.completionData?.participants);
    setIsParticipantModalVisible(true);
  };
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchAssessments();
  }, [pagination.current, pagination.pageSize]);

  const handleTableChange = (pageNav) => {
    setPagination(pageNav);
  };

  const columns = [
    {
      title: "Sl.No",
      key: "slNo",
      width: 80,
      align: "left",
      className: "vm__no-wrap",
      render: (_, record, index) => {
        return (pagination.current - 1) * pagination.pageSize + index + 1;
      },
    },
    {
      title: "Topic",
      dataIndex: "TopicName",
      key: "TopicName",
      className: "vm__min-w-lg",
    },
    {
      title: "Question count",
      dataIndex: "totalQuestions",
      key: "totalQuestions",
      width: 200,
      align: "center",
      className: "vm__no-wrap",
      render: (_, record) => {
        return parseInt(record?.completionData?.totalQuestions);
      },
    },
    {
      title: "Participants",
      dataIndex: "ParticipantsCount",
      key: "ParticipantsCount",
      width: 200,
      align: "center",
      className: "vm__no-wrap",
    },

    {
      title: "User Type",
      dataIndex: "UserType",
      key: "UserType",
      className: "vm__if-type vm__no-wrap",
      width: 100,
      render: (UserType) => {
        if (userTypeKey[UserType] === "Group") {
          return (
            <Tooltip title={userTypeKey[UserType]}>
              <span className="vm__table-icons">
                <UsergroupAddOutlined />
              </span>
            </Tooltip>
          );
        } else if (userTypeKey[UserType] === "Individual") {
          return (
            <Tooltip title={userTypeKey[UserType]}>
              <span className="vm__table-icons">
                <UserOutlined />
              </span>
            </Tooltip>
          );
        } else {
          return null; // Handle unknown types
        }
      },
    },

    {
      title: "Progress",
      dataIndex: "groupCompletionPercentage",
      key: "groupCompletionPercentage",
      className: "vm__column-progress vm__no-wrap",
      render: (_, record) => (
        <Tooltip
          title={record?.completionData?.groupCompletionPercentage + "%"}
          overlayStyle={{
            whiteSpace: "pre-line",
            maxWidth: 400,
          }}
        >
          <Progress
            percent={parseFloat(
              record?.completionData?.groupCompletionPercentage
            )}
          />
        </Tooltip>
      ),
    },

    {
      title: "Actions",
      key: "actions",
      width: 100,
      className: "vm__no-wrap",
      render: (_, record) => (
        <Tooltip title="View Participants">
          <Button
            type="text"
            icon={<EyeOutlined style={{ fontSize: "24px" }} />}
            onClick={() => handleViewParticipants(record)}
          />
        </Tooltip>
      ),
    },
  ];

  const participantColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      className: "vm__no-wrap",
      width: 200,
      render: (text) => text || "N/A",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      className: "vm__no-wrap",
      width: 200,
    },
    {
      title: "Progress",
      dataIndex: "individualCompletionPercentage",
      key: "individualCompletionPercentage",
      className: "vm__column-progress vm__no-wrap",
      render: (_, record) => (
        <Tooltip
          title={record?.individualCompletionPercentage + "%"}
          overlayStyle={{
            whiteSpace: "pre-line",
            maxWidth: 400,
          }}
        >
          <Progress
            percent={parseFloat(record?.individualCompletionPercentage)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="vm__assessment vm__h-100">
      <div className="vm__d-flex vm__flex-column vm__h-100">
        <div className="vm__flex-auto">
          <div className="vm__container">
            <div className="vm__d-flex vm__mb-3 vm__align-items-center vm__flex-wrap vm__gap-2">
              <div className="vm__flex-fill">
                <h3 className="vm__page-header vm__mb-0">Training</h3>
              </div>
            </div>
          </div>
        </div>
        <div className="vm__flex-fill vm__overflow-auto">
          <div className="vm__container">
            <Table
              dataSource={assessments}
              columns={columns}
              rowKey="AssessmentID"
              scroll={{ x: true }}
              className="vm__table-container"
              pagination={pagination}
              onChange={handleTableChange}
            />
          </div>
        </div>
      </div>

      {/* Participants Modal */}
      <Modal
        title={`Participants`}
        visible={isParticipantModalVisible}
        onCancel={() => setIsParticipantModalVisible(false)}
        footer={null}
        width={800}
        centered
        maskClosable={false}
      >
        <Table
          dataSource={selectedAssessment || []}
          columns={participantColumns}
          rowKey="UserID"
          pagination={false}
          className="vm__table-scroll"
        />
      </Modal>
    </div>
  );
};

export default AdminAssessmentManagement;
